import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import { siteSections } from "@/data/siteMap";
import { aboutContent } from "@/data/aboutContent";
import { solutionsContent } from "@/data/solutionsContent";
import { industriesContent } from "@/data/industriesContent";
import { careersContent } from "@/data/careersContent";

const contentBySection = {
  about: aboutContent,
  solutions: solutionsContent,
  industries: industriesContent,
  careers: careersContent,
};

export default function SectionPage({ section }) {
  const { slug } = useParams();
  const content = contentBySection[section]?.[slug];
  const sectionMeta = siteSections[section];

  if (!content) {
    return <Navigate to={`/${section}`} replace />;
  }

  return (
    <div className="bg-[#0B0C10]" data-testid={`page-${section}-${slug}`}>
      {/* HERO */}
      <section className="relative border-b border-[#1F2833] py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 te-grid-bg-dense opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <Breadcrumbs section={section} label={sectionMeta.items.find((i) => i.slug === slug)?.label || slug} />
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-6 mt-4">{content.kicker}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter leading-[0.95] max-w-5xl">
            {content.title}
          </h1>
          <p className="mt-6 font-display text-xl text-[#FF3355] max-w-3xl">{content.tagline}</p>
          <p className="mt-6 text-[#C5C6C7] max-w-3xl leading-relaxed">{content.intro}</p>

          {content.stats?.length > 0 && (
            <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#1F2833] border border-[#1F2833] max-w-4xl">
              {content.stats.map((s, i) => (
                <div key={i} className="bg-[#0B0C10] p-6">
                  <div className="font-display text-3xl text-[#FF3355] font-bold">{s.value}</div>
                  <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[#C5C6C7]">{s.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-20 lg:py-24 border-b border-[#1F2833]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-10">// CAPABILITIES</div>
          <div className="space-y-px bg-[#1F2833] border border-[#1F2833]">
            {content.pillars.map((p) => (
              <div key={p.id} className="bg-[#0B0C10] p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid={`pillar-${p.id}`}>
                <div className="lg:col-span-4">
                  <div className="font-mono text-xs uppercase tracking-[0.22em] text-[#FF3355]">[ {p.id} / {p.label} ]</div>
                </div>
                <div className="lg:col-span-8">
                  <p className="text-[#F5F5F7] leading-relaxed">{p.body}</p>
                  {p.bullets?.length > 0 && (
                    <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {p.bullets.map((b, i) => (
                        <li key={i} className="te-lime-accent flex items-start gap-3 font-mono text-sm text-[#C5C6C7]">
                          <span className="text-[#FF3355] mt-0.5">+</span>
                          <span>
                            <span className="text-[#F5F5F7]">{b.title}</span>
                            {b.desc && <span className="block text-xs opacity-70 mt-0.5">{b.desc}</span>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      {content.faqs?.length > 0 && (
        <section className="py-20 border-b border-[#1F2833]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-6">// FAQ</div>
            <div className="space-y-px bg-[#1F2833] border border-[#1F2833]">
              {content.faqs.map((f, i) => (
                <details key={i} className="bg-[#0B0C10] p-6 group" data-testid={`faq-${i}`}>
                  <summary className="cursor-pointer font-display text-lg font-semibold flex items-center justify-between gap-4 list-none">
                    <span>{f.q}</span>
                    <ChevronRight size={16} className="text-[#FF3355] group-open:rotate-90 transition-transform" />
                  </summary>
                  <p className="mt-4 text-[#C5C6C7] leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RELATED */}
      <section className="py-20 border-b border-[#1F2833]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-6">// RELATED · {sectionMeta.label}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-[#1F2833] border border-[#1F2833]">
            {sectionMeta.items.filter((i) => i.slug !== slug).slice(0, 8).map((it) => (
              <Link
                key={it.slug}
                to={`/${section}/${it.slug}`}
                data-testid={`related-${it.slug}`}
                className="bg-[#0B0C10] p-5 te-glow-hover border border-transparent hover:border-[#B91C2C] font-mono text-xs uppercase tracking-[0.18em] text-[#C5C6C7] hover:text-[#F5F5F7]"
              >
                {it.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 border border-[#1F2833] bg-[#0d1117] p-8 lg:p-12">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-3">// NEXT.STEP</div>
            <h3 className="font-display text-2xl lg:text-4xl font-bold tracking-tight">{content.cta.title}</h3>
            <p className="mt-3 text-[#C5C6C7]">{content.cta.sub}</p>
          </div>
          <Link
            to={content.cta.href}
            data-testid="section-cta"
            className="inline-flex items-center gap-2 bg-[#B91C2C] text-[#0B0C10] px-7 py-4 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] transition-colors w-fit"
          >
            {content.cta.title} <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}

function Breadcrumbs({ section, label }) {
  const sectionMeta = siteSections[section];
  return (
    <nav className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]/70" data-testid="breadcrumbs">
      <Link to="/" className="hover:text-[#FF3355]">home</Link>
      <span className="mx-2">/</span>
      <Link to={`/${section}`} className="hover:text-[#FF3355]">{sectionMeta.label}</Link>
      <span className="mx-2">/</span>
      <span className="text-[#FF3355]">{label}</span>
    </nav>
  );
}
